'use client';

import React, { useState, useRef, useEffect } from 'react';
import { TimelineDropdownItem } from './navigation-bar-horizontal';
import { WorkProduct, WorkProductType } from '@/shared/types';
import { FileTextIcon, CalendarDotsIcon, CalendarBlankIcon, UserIcon, ListBulletsIcon, SpinnerGapIcon, RocketIcon, TargetIcon } from '@phosphor-icons/react';
import { FetchWithVersionCheck, recordStat, formatTimelineDisplayText } from '@/shared/functions';
import { AuthContext, StatsContext } from '@/app/contexts';
import { useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from '@ag-grid-community/theming';
import { iconSetAlpine } from 'ag-grid-community';
export interface WorkProductIndexProps {
  caseId: string | undefined;
  timelines: TimelineDropdownItem[];
  onCreateTimeline: () => void;
  onSelectTimeline: (timelineId: string) => void;
  onSelectThreatReport?: () => void;
  featureFlags?: {
    [key: string]: any;
  };
  isLoading?: boolean;
  viewMode?: 'grid' | 'list';
  threatReportJson?: any;
  isThreatReportProcessing?: boolean;
  processingThreatReportId?: string | null;
  clearThreatReportKey: () => void;
}
const myTheme = themeQuartz.withPart(iconSetAlpine).withParams({
  accentColor: "#FFFFFF54",
  backgroundColor: "#0F1D32",
  borderColor: "#FFFFFF12",
  borderRadius: 20,
  browserColorScheme: "dark",
  cellHorizontalPaddingScale: 0.5,
  checkboxBorderRadius: "3px",
  chromeBackgroundColor: {
    ref: "backgroundColor"
  },
  columnBorder: false,
  fontFamily: "inherit",
  fontSize: "14px",
  foregroundColor: "#FCFCFC",
  headerBackgroundColor: "#09121E",
  headerFontSize: 16,
  headerFontWeight: 500,
  headerTextColor: "#FFFFFF",
  headerVerticalPaddingScale: .5,
  headerColumnResizeHandleColor: "#FFFFFF12",
  headerColumnResizeHandleWidth: 3,
  headerColumnResizeHandleHeight: 20,
  iconSize: "16px",
  inputBorder: false,
  inputBorderRadius: "0px",
  inputFocusBackgroundColor: "#FFFFFF00",
  inputFocusBorder: false,
  inputFocusShadow: "#0000002A 0px 0px 0px 3px",
  inputFocusTextColor: "#292A2C",
  inputTextColor: "#060606",
  oddRowBackgroundColor: "#FFFFFF10",
  rowBorder: false,
  rowVerticalPaddingScale: .5,
  sidePanelBorder: true,
  spacing: 8,
  wrapperBorder: true,
  wrapperBorderRadius: "7px"
});
const WorkProductIndex: React.FC<WorkProductIndexProps> = ({
  caseId,
  timelines,
  onCreateTimeline,
  onSelectTimeline,
  onSelectThreatReport,
  featureFlags,
  isLoading = false,
  viewMode = 'grid',
  threatReportJson,
  isThreatReportProcessing = false,
  processingThreatReportId = null,
  clearThreatReportKey
}) => {
  const [workProducts, setWorkProducts] = useState<WorkProduct[]>([]);
  const [lastThreatReportJson, setLastThreatReportJson] = useState<any>(null);
  const {
    idToken,
    accessToken,
    tenantName
  } = useContext(AuthContext);
  const statsContext = useContext(StatsContext);
  const gridRef = useRef<AgGridReact>(null);
  useEffect(() => {
    clearThreatReportKey();
    setLastThreatReportJson(threatReportJson);
  }, [threatReportJson]);
  useEffect(() => {
    try {
      FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId || '')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      }).then(async response => {
        if (response.ok) {
          const data = await response.json();
          if (data && data.epoch_time_secs) {
            setLastThreatReportJson(data);
          } else {
            setLastThreatReportJson(null);
          }
        } else {
          setLastThreatReportJson(null);
        }
      });
    } catch (error) {
      setLastThreatReportJson(null);
    }
  }, []);

  // Convert timelines to work products
  useEffect(() => {
    const timelineProducts: WorkProduct[] = timelines.map(timeline => ({
      id: timeline.id,
      type: (timeline.id.startsWith('threat-report-') ? 'threat-report' : 'timeline') as WorkProductType,
      title: formatTimelineDisplayText(timeline.title) || 'Untitled Timeline',
      description: formatTimelineDisplayText(timeline.description),
      isRead: false,
      // TODO: Implement read state tracking
      verificationState: 'unverified' as const,
      // TODO: Implement verification state
      createdAt: timeline.createdAt,
      createdBy: timeline.createdBy,
      resourceId: timeline.id,
      eventCount: timeline.eventCount
    }));

    // Add placeholder threat report only when there are timelines
    if (lastThreatReportJson && lastThreatReportJson.epoch_time_secs) {
      let innerThreatReportJson: any = {};
      if (lastThreatReportJson?.threat_report_json) {
        innerThreatReportJson = JSON.parse(lastThreatReportJson?.threat_report_json);
        innerThreatReportJson.description = innerThreatReportJson.title;
      } else if (lastThreatReportJson?.answer) {
        const status = 'status' in lastThreatReportJson ? lastThreatReportJson.status : undefined;
        const newAnswer = JSON.parse(lastThreatReportJson?.answer);
        const messages = newAnswer && 'messages' in newAnswer ? newAnswer.messages : undefined;
        if (status === 'completed') {
          innerThreatReportJson = {
            status: 'completed',
            description: 'Threat report completed...'
          };
        } else if (status === 'error') {
          innerThreatReportJson = {
            status: 'error',
            description: 'Error generating threat report...'
          };
        } else if (status === 'initializing') {
          innerThreatReportJson = {
            status: 'initializing',
            description: 'Threat report initializing...'
          };
        } else if (status === 'inprogress') {
          innerThreatReportJson = {
            status: 'inprogress',
            description: 'Threat report processing...'
          };
        } else if (!status || status === 'unknown') {
          if (newAnswer && 'messages' in newAnswer) {
            innerThreatReportJson = {
              status: 'inprogress',
              description: 'Threat report processing...'
            };
          } else if (newAnswer && 'output' in newAnswer) {
            innerThreatReportJson = {
              status: 'finalizing',
              description: 'Finalizing threat report...'
            };
          } else {
            innerThreatReportJson = {
              status: 'initializing',
              description: 'Threat report initializing...'
            };
          }
        } else {
          innerThreatReportJson = {
            status: 'unknown',
            description: 'Threat report status unknown...'
          };
        }
      }
      const threatReportPlaceholder: WorkProduct = {
        id: 'threat-report-placeholder',
        type: 'threat-report' as WorkProductType,
        title: 'Threat Report',
        status: innerThreatReportJson?.status || 'unknown',
        description: innerThreatReportJson?.description || 'Threat Report processing...',
        isRead: false,
        verificationState: 'unverified' as const,
        resourceId: 'threat-report-placeholder',
        createdAt: lastThreatReportJson?.timestamp
      };
      setWorkProducts([...timelineProducts, threatReportPlaceholder]);
    } else {
      setWorkProducts([...timelineProducts]);
    }
  }, [timelines, caseId, lastThreatReportJson]);
  const handleWorkProductClick = (product: WorkProduct) => {
    if (product.type === 'timeline' && product.resourceId) {
      onSelectTimeline(product.resourceId);
      recordStat('TimelineButtonClicked', new MouseEvent('click') as any, statsContext, !!featureFlags?.useIntercom);
    } else if (product.type === 'threat-report') {
      if (onSelectThreatReport) {
        onSelectThreatReport();
        recordStat('ThreatReportButtonClicked', new MouseEvent('click') as any, statsContext, !!featureFlags?.useIntercom);
      }
    }
  };
  const getTypeIcon = (type: WorkProductType) => {
    switch (type) {
      case 'timeline':
        return <CalendarDotsIcon size={24} weight="regular" />;
      case 'threat-report':
        return <FileTextIcon size={24} weight="regular" />;
      default:
        return <FileTextIcon size={24} weight="regular" />;
    }
  };
  const getTypeLabel = (type: WorkProductType) => {
    switch (type) {
      case 'timeline':
        return 'Timeline';
      case 'threat-report':
        return 'Threat Report';
      default:
        return 'Document';
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return '';
    }
  };
  const getCreatedByShort = (createdBy?: string) => {
    if (!createdBy) return '';
    return createdBy.split('@')[0];
  };

  // Column definitions for ag-grid list view
  const columnDefs = [{
    field: 'title',
    headerName: 'Title',
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      return <span className="text-white font-semibold">{formatTimelineDisplayText(product.title)}</span>;
    }
  }, {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      return <span className="text-white/65 text-sm">{formatTimelineDisplayText(product.description || '—')}</span>;
    }
  }, {
    field: 'type',
    headerName: 'Type',
    width: 150,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      return <span className="text-sm font-medium text-white">{getTypeLabel(product.type)}</span>;
    }
  }, {
    field: 'eventCount',
    headerName: 'Events',
    width: 120,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      if (product.type === 'timeline' && product.eventCount !== undefined) {
        return <span className="text-white/65 text-xs">{product.eventCount} {product.eventCount === 1 ? 'event' : 'events'}</span>;
      }
      return <span className="text-white/30 text-xs">—</span>;
    }
  }, {
    field: 'createdBy',
    headerName: 'Creator',
    width: 150,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      if (product.createdBy) {
        return <span className="text-white/65 text-xs">{getCreatedByShort(product.createdBy)}</span>;
      }
      return <span className="text-white/30 text-xs">—</span>;
    }
  }, {
    field: 'createdAt',
    headerName: 'Created',
    width: 150,
    cellRenderer: (params: any) => {
      const product = params.data as WorkProduct;
      // Check if this threat report is currently processing
      const isProcessing = isThreatReportProcessing && product.type === 'threat-report' && product.id === processingThreatReportId;
      if (isProcessing) {
        return <div className="flex flex-row items-center gap-1 rounded-[4px] px-2 py-1" style={{
          backgroundColor: 'rgb(45, 141, 254)'
        }} aria-live="polite" role="status">
              <SpinnerGapIcon size={12} weight="bold" className="text-white animate-spin" aria-hidden="true" />
              <span className="text-white font-bold text-xs">Generating…</span>
            </div>;
      }
      if (product.createdAt) {
        return <span className="text-white/65 text-xs">{formatDate(product.createdAt)}</span>;
      } else if (product.type === 'threat-report') {
        return <span className="text-white/30 text-xs">—</span>;
      }
      return <span className="text-white/30 text-xs">—</span>;
    }
  }];
  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: false,
    cellStyle: {
      display: 'flex',
      alignItems: 'center'
    }
  };
  const onRowClicked = (params: any) => {
    const product = params.data as WorkProduct;
    handleWorkProductClick(product);
  };

  // Feature flag check
  if (!featureFlags?.workProductNav) {
    return null;
  }
  return <div className="w-full h-full bg-[#0F1D32] p-[30px] mt-[30px] sm:mt-0 box-border" data-sentry-component="WorkProductIndex" data-sentry-source-file="work-product-index.tsx">
      {isLoading ? <div className="flex flex-col items-center justify-center min-h-[400px]" aria-live="polite" aria-busy="true" role="status">
          <SpinnerGapIcon size={48} className="text-white animate-spin mb-4" aria-hidden="true" />
          <p className="text-white/70 text-lg">Loading work products...</p>
        </div> : workProducts.length === 0 ? <div className="flex flex-col items-center justify-center min-h-[400px] bg-white/5 rounded-lg border border-white/10">
          <p className="text-white/70 text-lg mb-6">No work products yet</p>
        </div> : viewMode === 'list' ? <div className="w-full h-full ag-theme-quartz" role="region" aria-label="Work products table">
          <AgGridReact ref={gridRef} rowData={workProducts} columnDefs={columnDefs as any} defaultColDef={defaultColDef as any} onRowClicked={onRowClicked} gridOptions={{
        suppressScrollOnNewData: true,
        rowStyle: {
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center'
        },
        getRowHeight: () => 60
      }} theme={myTheme} />
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-full">
          {workProducts.map(product => <div key={product.id} role="button" tabIndex={0} aria-label={`${getTypeLabel(product.type)}: ${formatTimelineDisplayText(product.title)}`} onClick={() => handleWorkProductClick(product)} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleWorkProductClick(product);
        }
      }} className="bg-white/5 hover:bg-white/15 border border-white/7 rounded-lg p-6 cursor-pointer transition-all hover:border-[4px] hover:border-white hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)] group">
              <div className="flex flex-row items-start justify-between mb-4">
                <div className="flex flex-row items-center gap-2 text-white/70 group-hover:text-white transition-colors">
                  {getTypeIcon(product.type)}
                  <span className="text-sm font-medium">{getTypeLabel(product.type)}</span>
                </div>
                {/* Temporarily hidden: verification status and read status indicators */}
                {/* <div className="flex flex-row items-center gap-2">
                  {product.verificationState === 'verified' ? (
                    <CheckCircleIcon size={20} className="text-green-500" weight="fill" />
                  ) : (
                    <CircleIcon size={20} className="text-white/30" weight="duotone" />
                  )}
                  {!product.isRead && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                 </div> */}
              </div>

              <h3 className="text-white font-semibold text-xl mb-1 group-hover:text-blue-300 transition-colors">
                {formatTimelineDisplayText(product.title)}
              </h3>

              {product.description && <div className="pt-2 flex flex-row items-center gap-1 group-hover:text-white transition-colors">
                  {product.status && ['processing', 'inprogress', 'finalizing', 'initializing'].includes(product.status) && <div className="text-white/65 transition-colors mt-[1px] -mb-[1px]">
                      {product.status === 'processing' || product.status === 'inprogress' ? <SpinnerGapIcon size={18} weight="bold" className="text-white animate-spin" /> : product.status === 'finalizing' ? <TargetIcon size={18} weight="bold" className="text-white animate-blink" /> : product.status === 'initializing' ? <RocketIcon size={18} weight="bold" className="text-white animate-bounce" /> : null}
                    </div>}
                  <div className="text-white/65 group-hover:text-white text-sm line-clamp-2 transition-colors">
                    {formatTimelineDisplayText(product.description || '')}
                  </div>
                </div>}

              <div className="flex flex-col lg:flex-row items-start lg:items-center lg:justify-between gap-2 text-white/65 group-hover:text-white text-xs mt-4 pt-4 border-t border-white/10 transition-colors">
                {product.type === 'timeline' && product.eventCount !== undefined && <div className="flex flex-row items-center gap-1">
                    <ListBulletsIcon size={14} weight="bold" />
                    <span>{product.eventCount} {product.eventCount === 1 ? 'event' : 'events'}</span>
                  </div>}
                {product.createdBy && <div className="flex flex-row items-center gap-1">
                    <UserIcon size={14} weight="bold" />
                    <span>{getCreatedByShort(product.createdBy)}</span>
                  </div>}
                {(() => {
            // Check if this threat report is currently processing
            const isProcessing = isThreatReportProcessing && product.type === 'threat-report' && product.id === processingThreatReportId;
            if (isProcessing) {
              return <div className="flex flex-row items-center gap-1 rounded-[4px] px-2 py-1" style={{
                backgroundColor: 'rgb(45, 141, 254)'
              }} aria-live="polite" role="status">
                        <SpinnerGapIcon size={12} weight="bold" className="text-white animate-spin" aria-hidden="true" />
                        <span className="text-white font-bold text-xs">Generating…</span>
                      </div>;
            }
            if (product.createdAt) {
              return <div className="flex flex-row items-center gap-1">
                        <CalendarBlankIcon size={14} weight="bold" />
                        <span>{formatDate(product.createdAt)}</span>
                      </div>;
            } else if (product.type === 'threat-report') {
              return <div className="flex flex-row items-center gap-1">
                        <CalendarBlankIcon size={14} weight="bold" />
                        <span>—</span>
                      </div>;
            }
            return null;
          })()}
              </div>
            </div>)}
        </div>}
    </div>;
};
export default WorkProductIndex;